import { fiMetaForArbitrumInternalRow, fiMetaForCronosInternalRow } from './fiCronosLedger';
import type { LedgerEvent } from './types';

function normAddr(a?: string): string | null {
  if (!a) return null;
  const t = String(a).trim().toLowerCase();
  return /^0x[a-f0-9]{40}$/.test(t) ? t : null;
}

/**
 * Exchange **deposit hot wallets** are shared — do not list them as owned addresses.
 * After normal Cronos classification, rewrite simple legs that touch a configured CEX deposit
 * address so they match Finnish practice: moving crypto to/from your own exchange account is
 * a non-taxable transfer (FIFO excluded), not a disposal/acquisition on-chain.
 *
 * Only **plain** `TRANSFER_OUT` / `TRANSFER_IN` rows are rewritten — not `SWAP` or other kinds.
 */
export function applyCexDepositCounterpartyClassification(
  events: LedgerEvent[],
  options: { ownedAddresses: Set<string>; cexDepositAddresses: Set<string> }
): { events: LedgerEvent[]; reclassified: number } {
  const { ownedAddresses, cexDepositAddresses } = options;
  if (cexDepositAddresses.size === 0) {
    return { events, reclassified: 0 };
  }

  let reclassified = 0;
  const out = events.map((ev) => {
    if (ev.source !== 'cronos' && ev.source !== 'arbitrum') return ev;
    const fiCex = ev.source === 'arbitrum' ? fiMetaForArbitrumInternalRow : fiMetaForCronosInternalRow;
    const subOut =
      ev.source === 'arbitrum' ? 'ARBITRUM_CEX_DEPOSIT_COUNTERPARTY' : 'CRONOS_CEX_DEPOSIT_COUNTERPARTY';
    const subIn =
      ev.source === 'arbitrum' ? 'ARBITRUM_CEX_WITHDRAWAL_COUNTERPARTY' : 'CRONOS_CEX_WITHDRAWAL_COUNTERPARTY';
    const f = normAddr(ev.fromAddress);
    const t = normAddr(ev.toAddress);
    if (!f || !t) return ev;

    const simpleOut =
      ev.kind === 'disposal' &&
      ev.classification === 'TRANSFER_OUT' &&
      ownedAddresses.has(f) &&
      !ownedAddresses.has(t) &&
      cexDepositAddresses.has(t);

    const simpleIn =
      ev.kind === 'acquisition' &&
      ev.classification === 'TRANSFER_IN' &&
      cexDepositAddresses.has(f) &&
      ownedAddresses.has(t) &&
      !ownedAddresses.has(f);

    if (simpleOut) {
      reclassified++;
      return {
        ...ev,
        kind: 'internal_transfer' as const,
        classification: 'INTERNAL_TRANSFER' as const,
        eurValue: null,
        cronosPriceHint: undefined,
        proceedsDerivation: undefined,
        valuationFlag: undefined,
        ...fiCex('INTERNAL_TRANSFER', {
          fiSubreason: subOut,
        }),
        explanation: [
          ev.explanation,
          'Reclassified: outgoing transfer to a configured CEX deposit address (non-taxable movement; confirm with Vero).',
        ]
          .filter(Boolean)
          .join(' '),
        reviewFlags: Array.from(
          new Set([...(ev.reviewFlags ?? []), 'cex_deposit_counterparty'])
        ),
      };
    }

    if (simpleIn) {
      reclassified++;
      return {
        ...ev,
        kind: 'internal_transfer' as const,
        classification: 'INTERNAL_TRANSFER' as const,
        eurValue: null,
        cronosPriceHint: undefined,
        proceedsDerivation: undefined,
        valuationFlag: undefined,
        ...fiCex('INTERNAL_TRANSFER', {
          fiSubreason: subIn,
        }),
        explanation: [
          ev.explanation,
          'Reclassified: incoming transfer from a configured CEX deposit address (non-taxable movement; confirm with Vero).',
        ]
          .filter(Boolean)
          .join(' '),
        reviewFlags: Array.from(
          new Set([...(ev.reviewFlags ?? []), 'cex_withdrawal_counterparty'])
        ),
      };
    }

    return ev;
  });

  return { events: out, reclassified };
}
